import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Link, useHistory } from "react-router-dom";
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Row,
  Col,
  Spinner,
  Badge,
  Button,
} from "reactstrap";
import { withTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { postRequest } from "components/Common/Utils";

const NotificationDropdown = (props) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalNotifications, setTotalNotifications] = useState(0);
  const history = useHistory();

  useEffect(() => {
    if (dropdownOpen) {
      fetchNotifications();
    }
  }, [dropdownOpen]);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      setError(null);

      const payload = {
        page: 1,
        limit: 100,
        search: "",
        sortField: "createdAt",
        sortOrder: "desc",
      };

      const response = await postRequest("/notification/admin-list", payload);

      if (response.status && response.data) {
        const sortedNotifications = (response.data.notifications || []).sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
        setNotifications(sortedNotifications);
        setTotalNotifications(response.data.total || 0);
      } else {
        throw new Error(response.message || "Failed to load notifications");
      }
    } catch (err) {
      setError(err.message || "Failed to load notifications");
    } finally {
      setLoading(false);
    }
  };

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const handleNotificationClick = async (notification) => {
    try {
      if (!notification.readStatus) {
        await postRequest(`/notification/admin-read`, {
          notificationId: notification._id,
        });
        setNotifications(
          notifications.map((n) =>
            n._id === notification._id ? { ...n, readStatus: true } : n
          )
        );
      }

      if (notification.type === "order" && notification.typeId) {
        history.push(`/user-order-details/${notification.typeId}`);
      } else if (
        notification.type === "manufacture-order" &&
        notification.typeId
      ) {
        history.push(`/order-sent-manufacturer/${notification.typeId}`);
      }
    } catch (err) {
      console.error("Error handling notification click:", err);
    }
  };

  useEffect(() => {
    fetchNotifications();
    // const interval = setInterval(fetchNotifications, 10000); // refresh every 30
    return () => clearInterval(interval);
  }, []);

  const unreadCount = notifications.filter((n) => !n.readStatus).length;

  const handleViewAll = () => {
    setDropdownOpen(false);
    history.push("/notifications-details");
  };

  return (
    <Dropdown
      isOpen={dropdownOpen}
      toggle={toggleDropdown}
      className="dropdown d-inline-block ms-1"
      tag="li"
    >
      <DropdownToggle
        tag="button"
        className="btn header-item noti-icon waves-effect"
        id="page-header-notifications-dropdown"
        style={{ position: "relative" }}
      >
        <i className="ti-bell"></i>
        {unreadCount > 0 && (
          <Badge
            color="danger"
            pill
            style={{
              position: "absolute",
              right: "5px",
              fontSize: "12px",
              minWidth: "18px",
              height: "18px",
              lineHeight: "18px",
              padding: 0,
              display: "inline-block",
              textAlign: "center",
              borderRadius: "50%",
              backgroundColor: "red",
              color: "white",
              zIndex: 10,
            }}
          >
            {unreadCount}
          </Badge>
        )}
      </DropdownToggle>

      <DropdownMenu end className="dropdown-menu-lg dropdown-menu-end p-0">
        <div className="p-3 border-bottom">
          <Row className="align-items-center">
            <Col>
              <h6 className="m-0">Notifications</h6>
            </Col>
            <Col className="text-end">
              <Button
                color="link"
                size="sm"
                onClick={handleViewAll}
                className="p-0"
              >
                View All
              </Button>
            </Col>
          </Row>
        </div>

        <div style={{ maxHeight: "230px", overflowY: "auto" }}>
          {loading ? (
            <div className="text-center py-3">
              <Spinner size="sm" color="primary" />
              <span className="ms-2">Loading notifications...</span>
            </div>
          ) : error ? (
            <div className="text-center py-3 text-danger">
              {error}
              <button
                className="btn btn-sm btn-link"
                onClick={fetchNotifications}
              >
                Retry
              </button>
            </div>
          ) : notifications.length > 0 ? (
            notifications.slice(0, 5).map((notification) => (
              <DropdownItem
                key={notification._id}
                className={`${!notification.readStatus ? "bg-light" : ""}`}
                onClick={() => handleNotificationClick(notification)}
                tag="div"
                style={{ cursor: "pointer" }}
              >
                <div className="d-flex">
                  <div className="flex-grow-1">
                    <h6 className="mb-1">{notification.title}</h6>
                    <p
                      className="mb-1 text-truncate"
                      style={{ maxWidth: "250px" }}
                    >
                      {notification.description}
                    </p>
                    <small className="text-muted">
                      {new Date(notification.createdAt).toLocaleString()}
                    </small>
                  </div>
                  {notification.image && (
                    <div className="flex-shrink-0 ms-2">
                      <img
                        src={notification.image}
                        alt="notification"
                        className="img-thumbnail"
                        style={{ width: "40px", height: "40px" }}
                      />
                    </div>
                  )}
                </div>
              </DropdownItem>
            ))
          ) : (
            <DropdownItem className="text-center py-3">
              No notifications found
            </DropdownItem>
          )}
        </div>
      </DropdownMenu>
    </Dropdown>
  );
};

export default withTranslation()(NotificationDropdown);

NotificationDropdown.propTypes = {
  t: PropTypes.any,
};